/**
 * Sara desktop-browser opnamepad — client-side plumbing (ADR 0009 punt 3,
 * bouwplan fase 4.2). Drie brokken die de UI (`pages/Sara.tsx`) aan elkaar
 * wiret:
 *
 *   1. Types voor het Sara-taskcontract (backend commit 4.1 levert ze later
 *      in @kennisbank/shared; tot die tijd lokaal zodat de merge klein blijft).
 *   2. Crash-bestendige buffering: MediaRecorder-chunks naar IndexedDB tijdens
 *      de opname, zodat een tab-crash of herlaad geen heel consult kost.
 *   3. Hervatbare upload naar een GCS resumable signed URL, met retry/backoff
 *      en server-gesynchroniseerde offset na een netwerkfout.
 *
 * De upload praat rechtstreeks met GCS (cross-origin, géén credentials); alle
 * eigen-API-calls lopen via `api/client.ts`.
 */
import {
  SaraAudioMimeTypeInput,
  type SaraAudioMimeType,
  type SaraSoep,
  type SaraSoepStyle,
  type SaraTaskListItem,
  type SaraTaskView,
} from '@kennisbank/shared';
import { apiDelete, apiGet, apiPost } from '../api/client.js';

// ── Taskcontract ─────────────────────────────────────────────────────────

// De taak-, SOEP- en statustypes komen uit @kennisbank/shared (schemas/
// sara-task.ts) zodat frontend en backend op één contract zitten. `SaraTaskView`
// is de subset die GET /tasks/:taskId teruggeeft; `error` is daar een
// { code, message }-object, geen kale string.
export type { SaraSoep, SaraTaskListItem, SaraTaskStatus, SaraTaskView } from '@kennisbank/shared';

interface UploadUrlResponse {
  taskId: string;
  uploadUrl: string;
  /**
   * Headers die de backend in de handtekening van de signed URL heeft
   * meegetekend (o.a. x-goog-content-length-range). GCS eist dat de client ze
   * exact zo meestuurt bij het starten van de sessie, anders 403.
   */
  uploadHeaders?: Record<string, string>;
}

// ── Eigen-API-calls ──────────────────────────────────────────────────────

export function requestUploadUrl(
  mimeType: string,
  durationSec: number,
): Promise<UploadUrlResponse> {
  return apiPost<UploadUrlResponse>('/api/v1/sara/recordings/upload-url', {
    mimeType,
    durationSec,
  });
}

/**
 * Opwarmseintje bij opnamestart: de GPU-instance van de transcriber start op
 * tijdens het gesprek, zodat de koude start niet bij de wachttijd na het
 * stoppen optelt. Fouten negeert de caller (best-effort).
 */
export function warmupTranscriber(): Promise<unknown> {
  return apiPost<unknown>('/api/v1/sara/warmup', {});
}

export function finalizeRecording(taskId: string): Promise<unknown> {
  return apiPost<unknown>(`/api/v1/sara/recordings/${taskId}/finalize`, {});
}

export function fetchSaraTask(taskId: string): Promise<SaraTaskView> {
  return apiGet<SaraTaskView>(`/api/v1/sara/tasks/${taskId}`);
}

/** Eigen recente taken (alleen metadata) — om een opname binnen de 72u-retentie
 *  terug te vinden en te heropenen. */
export function fetchSaraTasks(): Promise<{ tasks: SaraTaskListItem[] }> {
  return apiGet<{ tasks: SaraTaskListItem[] }>('/api/v1/sara/tasks');
}

/** Wis een eigen opname (doc + audio) vóór de automatische 72u-opruiming. */
export function deleteSaraTask(taskId: string): Promise<void> {
  return apiDelete(`/api/v1/sara/tasks/${taskId}`);
}

export function approveSaraTask(taskId: string, soep: SaraSoep): Promise<unknown> {
  return apiPost<unknown>(`/api/v1/sara/tasks/${taskId}/approve`, { soep });
}

/** Genereer het SOEP opnieuw in de gevraagde stijl (72u, vanuit review). */
export function regenerateSaraTask(taskId: string, soepStyle: SaraSoepStyle): Promise<unknown> {
  return apiPost<unknown>(`/api/v1/sara/tasks/${taskId}/regenerate`, { soepStyle });
}

// ── Opnameformaat ────────────────────────────────────────────────────────

/**
 * De twee containers waarin een browser hier mag opnemen (`SaraAudioMimeType`
 * uit @kennisbank/shared): WebM/Opus is wat Chrome, Edge en Firefox leveren,
 * MP4/AAC is wat Safari levert, op iOS én op de Mac. Meer smaken zijn er
 * bewust niet: de backend tekent de signed URL op dit basistype en leidt de
 * bestandsextensie eruit af, dus alles wat hier binnenkomt moet daar een
 * tegenhanger hebben.
 *
 * Voorkeursvolgorde voor MediaRecorder: eerst WebM/Opus (kleiner en beter voor
 * spraak), dan MP4/AAC voor browsers die geen WebM kunnen opnemen. Beide met
 * en zonder codec-parameter, want sommige browsers ondersteunen alleen de kale
 * containernotatie in `isTypeSupported`.
 */
const RECORD_MIME_PREFS = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
] as const;

function mediaRecorderSupports(type: string): boolean {
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
}

/**
 * Het opnametype voor deze browser, of null als geen van beide containers kan.
 * De caller toont bij null een melding in plaats van de MediaRecorder-
 * constructor te laten gooien.
 *
 * `isTypeSupported` is injecteerbaar omdat de frontendtests in node draaien,
 * waar MediaRecorder niet bestaat.
 */
export function pickRecordMime(
  isTypeSupported: (type: string) => boolean = mediaRecorderSupports,
): string | null {
  for (const type of RECORD_MIME_PREFS) {
    if (isTypeSupported(type)) return type;
  }
  return null;
}

/**
 * Het basistype (zonder codec-parameters) van een opnametype. Alles wat de
 * browser uit gaat gebruikt dít en niet de string uit `pickRecordMime`: de
 * backend ondertekent de signed URL op het basistype, dus een Content-Type met
 * `;codecs=opus` erachter levert een 403 op de GCS-sessie.
 *
 * Het strippen zelf komt uit `SaraAudioMimeTypeInput` (@kennisbank/shared), de
 * normalisatie die de backend op dezelfde invoer toepast. Twee keer hetzelfde
 * uitprogrammeren is precies hoe client en handtekening uiteen gaan lopen.
 *
 * Onbekende of lege invoer valt terug op audio/webm. Dat is geen inhoudelijke
 * gok: `pickRecordMime` is de enige bron van verse opnames en levert alleen de
 * twee containers hierboven, en een blob zonder type komt uit een crash-buffer
 * van vóór het mobiele pad, toen WebM het enige formaat was.
 */
export function baseMimeType(recordMime: string): SaraAudioMimeType {
  const parsed = SaraAudioMimeTypeInput.safeParse(recordMime);
  return parsed.success ? parsed.data : 'audio/webm';
}

// ── Apparaatdetectie ─────────────────────────────────────────────────────

/**
 * Draait dit op een telefoon of tablet? Stuurt de voorlichting vooraf en de
 * strengheid van de onderbrekingsmelding tijdens de opname (`pages/Sara.tsx`).
 * Opnemen wordt hier niet meer op geweigerd: dat blokkeerde ook de POH die zijn
 * telefoon netjes op de voorgrond houdt.
 *
 * De user-agent alleen is niet genoeg, want twee soorten tablets vermommen
 * zich als desktop en zouden de voorlichting daardoor mislopen:
 *
 *   - iPadOS 13+ in Safari meldt "Macintosh; Intel Mac OS X 10_15_7", zonder
 *     iPad, iPhone of Mobile erin.
 *   - een Android-tablet met "Site voor desktop aanvragen" meldt
 *     "X11; Linux x86_64", zonder Android en zonder Mobile.
 *
 * Allebei melden ze wél maxTouchPoints 5. Het aanraakscherm is daarom het
 * hoofdsignaal en de user-agent de uitzonderingslijst, niet andersom: alleen
 * bij een besturingssysteem dat legitiem als touch-desktop bestaat, is een
 * aanraakscherm géén bewijs van een tablet.
 *
 * Dit is de derde ronde op deze functie; de al afgewogen gevallen:
 *
 *   - drempel >1 en niet >0: een losse touchmonitor of tekentablet aan een Mac
 *     kan 1 melden. Een echte Mac meldt 0, want Apple verkoopt geen Mac met
 *     aanraakscherm en een Magic Trackpad is een aanwijsapparaat, geen touch.
 *   - Windows (ook Surface): laptops met aanraakscherm draaien desktop-Chrome
 *     of -Edge, die de opname niet pauzeren. Geen mobiel.
 *   - ChromeOS ("CrOS", ook convertibles in tabletstand): draait dezelfde
 *     desktop-Chrome met dezelfde MediaRecorder. Geen mobiel.
 *   - Linux/X11-desktop met aanraakscherm: die telt hier wél als mobiel,
 *     bewust. Zijn user-agent is letterlijk gelijk aan die van een
 *     Android-tablet in desktopmodus, dus er valt niets te onderscheiden en het
 *     is kiezen welke fout je maakt. Sinds opnemen op mobiel is toegestaan zijn
 *     de kosten van die fout klein en eenzijdig: een gemiste tablet betekent
 *     dat de POH de waarschuwing over app-wisselen niet ziet, een onterecht als
 *     mobiel geteld bureaublad ziet één regel uitleg te veel.
 */
export function isMobileDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)) return true;
  if (/Windows|CrOS/i.test(userAgent)) return false;
  return maxTouchPoints > 1;
}

// ── Plausibiliteit van een opname ────────────────────────────────────────

// Opus-spraak levert ruwweg 2-4 kB/s. Ver daaronder betekent dat de browser de
// opname gepauzeerd heeft of dat de microfoon niets doorgaf. 16 kB is ongeveer
// vijf seconden spraak.
//
// Deze twee drempels blijven formaat-onafhankelijk, ook nu Safari in MP4/AAC
// opneemt. Ze zijn ondergrenzen, en AAC uit MediaRecorder ligt met zijn
// gebruikelijke 64-128 kbit/s (8-16 kB/s) een factor hoger dan Opus: elke
// AAC-opname met echte audio zit daarmee verder bóven de drempel dan een
// Opus-opname. Meebewegen met het formaat zou dus betekenen: de drempel
// omhoog voor MP4. Dat maakt de enige onomkeerbare uitkomst ('empty' wist de
// crash-buffer) agressiever, terwijl de winst alleen zit in het eerder
// opmerken van een half mislukte opname. Die winst halen we nu ergens anders
// vandaan: de gatendetectie in pages/Sara.tsx meet de onderbreking direct aan
// de chunk-tussenpozen en is niet van bitrate afhankelijk. De prijs is dat de
// controle op MP4 minder gevoelig is (een AAC-opname die 80% van het consult
// mist kan nog steeds boven 1 kB/s uitkomen en 'ok' heten). Dat is bewust: die
// kant faalt naar bewaren en niet naar wissen.
const MIN_AUDIO_BYTES = 16 * 1024;
const MIN_BYTES_PER_SEC = 1000;
/**
 * Onder deze opnameduur zegt de omvang van de blob te weinig: een korte opname
 * bestaat voor een groot deel uit containerheaders, en vijf seconden spraak
 * blijft op 2-4 kB/s legitiem onder MIN_AUDIO_BYTES. Deze grens bepaalt daarom
 * twee dingen tegelijk: vanaf hier telt de bytes-per-seconde-verhouding mee,
 * en vanaf hier mag "weinig bytes" zeker heten (zie `classifyRecording`).
 * Geëxporteerd omdat de UI dezelfde grens gebruikt om een korte opname anders
 * uit te leggen dan een lang consult dat halverwege stilviel.
 */
export const MIN_RATE_CHECK_SEC = 30;

/**
 * Onder deze opnameduur is een opname geen bewuste opname meer maar een
 * klik-ongelukje: starten en meteen weer stoppen. Twee seconden, om twee
 * redenen.
 *
 * Naar boven: de grens moet ruim onder alles liggen wat een POH bewust kan
 * inspreken. Een korte aanvulling ("cliënt belt volgende week zelf voor een
 * vervolgafspraak") duurt enkele seconden, dus vanaf 2 seconden bewaren we.
 * Wat korter is dan dat past niet eens om één zin heen.
 *
 * Naar beneden: de duur komt uit een `Math.floor` op de wandklok, dus alles
 * tussen 0 en 1,99 seconden meldt zich als 0 of 1. Een grens van 2 gooit
 * daarmee precies de start-stop-klik weg en niets daarboven.
 *
 * De kosten van beide fouten zijn hier ongelijk, en dat is wat de grens laag
 * houdt: onterecht weggooien kost hooguit één seconde audio en de POH kan
 * meteen opnieuw opnemen, terwijl onterecht bewaren hem een foutpaneel voor
 * een misklik geeft plus een herstelprompt bij de volgende paginalaad.
 */
const MIN_INTENTIONAL_SEC = 2;

export type RecordingCheck = 'ok' | 'empty' | 'thin' | 'negligible';

/**
 * Hoe bruikbaar is deze opname? Zonder deze poort levert de pijplijn een leeg
 * SOEP-concept op en weet de POH niet dat het consult verloren is.
 *
 * De afkeurende uitkomsten zijn bewust gescheiden, want ze verschillen in
 * zekerheid en mogen daarom niet dezelfde gevolgen hebben:
 *
 *   'empty':      zekerheid. Er is niets opgeslagen (nul bytes), of de opname
 *                 duurde lang genoeg dat een handvol kB onmogelijk echte audio
 *                 kan zijn. Daar valt niets te redden, dus de caller mag de
 *                 crash-buffer wissen en legt uit wat er misging.
 *   'thin':       schatting. Er zit mogelijk wél audio in, alleen minder dan bij
 *                 deze duur verwacht. Een consult van 45 minuten waarbij de
 *                 browser na vier minuten naar de achtergrond ging levert
 *                 ongeveer 1 MB bij durationSec 2700: ruim onder de drempel, en
 *                 toch vier echte minuten. De caller moet die buffer dus laten
 *                 staan en de gebruiker laten kiezen.
 *   'negligible': ook zekerheid, maar van een andere soort dan 'empty'. Er is
 *                 niets mislukt; er is per ongeluk gestart en meteen gestopt
 *                 (zie MIN_INTENTIONAL_SEC). De caller ruimt stil op en gaat
 *                 terug naar de beginstand: geen foutpaneel, want er valt niets
 *                 uit te leggen, en geen achtergebleven buffer, want dan biedt
 *                 de volgende paginalaad de misklik als herstel aan.
 *
 * Een onomkeerbare verwijdering mag nooit op een schatting rusten. De bytetoets
 * alleen was zo'n schatting en stond toch aan de wis-kant: Opus loopt op 2-4
 * kB/s, dus een bewust ingesproken aanvulling van een paar seconden blijft
 * legitiem onder MIN_AUDIO_BYTES en werd gewist zonder herstelweg (feedback
 * Marc, 6 aug 2026). De duur is daarom de poort naar de wis-kant, aan beide
 * uiteinden: pas vanaf MIN_RATE_CHECK_SEC is 16 kB werkelijk onmogelijk (dat is
 * nog geen halve kB per seconde, terwijl zelfs de zuinigste Opus-instelling ruim
 * 1 kB/s levert), en onder MIN_INTENTIONAL_SEC kan er geen bewuste opname aan
 * vooraf zijn gegaan. Daartussen blijft het een schatting en dus 'thin':
 * bewaren en de POH laten kiezen.
 */
export function classifyRecording(bytes: number, durationSec: number): RecordingCheck {
  // Nul bytes is geen schatting maar een feit: er is niets om te verliezen.
  // Bewust géén 'negligible', ook niet bij een opname van één seconde: nul bytes
  // betekent dat de microfoon niets doorgaf, en dat mag je niet stil wegpoetsen
  // want dan neemt de POH straks een heel consult op met dezelfde stille
  // microfoon.
  if (bytes === 0) return 'empty';
  if (bytes < MIN_AUDIO_BYTES) {
    if (durationSec >= MIN_RATE_CHECK_SEC) return 'empty';
    // Alleen binnen deze band mag stil weggooien: te kort om bewust te zijn én
    // te weinig bytes om iets van waarde te bevatten. Zit er ondanks de korte
    // duur toch veel audio in, dan valt de opname hieronder gewoon door naar
    // 'ok' en gooien we niets weg.
    if (durationSec < MIN_INTENTIONAL_SEC) return 'negligible';
    return 'thin';
  }
  if (durationSec >= MIN_RATE_CHECK_SEC && bytes < durationSec * MIN_BYTES_PER_SEC) return 'thin';
  return 'ok';
}

// ── IndexedDB-buffering ──────────────────────────────────────────────────

const DB_NAME = 'sara-recording';
const DB_VERSION = 1;
const CHUNK_STORE = 'chunks';
const META_STORE = 'meta';
const META_KEY = 'current';

export interface BufferMeta {
  mimeType: string;
  durationSec: number;
  savedAt: number;
}

export interface BufferedRecording {
  meta: BufferMeta;
  blob: Blob;
}

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        db.createObjectStore(CHUNK_STORE, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function runTx(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode,
  fn: (tx: IDBTransaction) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(stores, mode);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
    fn(tx);
  });
}

/** Start een verse buffersessie: eerdere chunks weg, meta gezet. */
export async function bufferInit(mimeType: string): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  try {
    await runTx(db, [CHUNK_STORE, META_STORE], 'readwrite', (tx) => {
      tx.objectStore(CHUNK_STORE).clear();
      const meta: BufferMeta = { mimeType, durationSec: 0, savedAt: Date.now() };
      tx.objectStore(META_STORE).put(meta, META_KEY);
    });
  } finally {
    db.close();
  }
}

/** Voeg één opnamechunk toe en werk de duur bij (idempotent op meta). */
export async function bufferAppend(
  chunk: Blob,
  mimeType: string,
  durationSec: number,
): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  try {
    await runTx(db, [CHUNK_STORE, META_STORE], 'readwrite', (tx) => {
      tx.objectStore(CHUNK_STORE).add(chunk);
      const meta: BufferMeta = { mimeType, durationSec, savedAt: Date.now() };
      tx.objectStore(META_STORE).put(meta, META_KEY);
    });
  } finally {
    db.close();
  }
}

/** Laad een achtergebleven opname terug (voor herstel na herlaad/crash). */
export async function bufferLoad(): Promise<BufferedRecording | null> {
  if (!hasIndexedDb()) return null;
  const db = await openDb();
  try {
    const meta = await new Promise<BufferMeta | undefined>((resolve, reject) => {
      const req = db.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(META_KEY);
      req.onsuccess = () => resolve(req.result as BufferMeta | undefined);
      req.onerror = () => reject(req.error);
    });
    if (!meta) return null;
    const chunks = await new Promise<Blob[]>((resolve, reject) => {
      const req = db.transaction(CHUNK_STORE, 'readonly').objectStore(CHUNK_STORE).getAll();
      req.onsuccess = () => resolve(req.result as Blob[]);
      req.onerror = () => reject(req.error);
    });
    if (chunks.length === 0) return null;
    return { meta, blob: new Blob(chunks, { type: meta.mimeType }) };
  } finally {
    db.close();
  }
}

/** Wis de buffer. Aangeroepen zodra de upload bevestigd is (finalize gelukt). */
export async function bufferClear(): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  try {
    await runTx(db, [CHUNK_STORE, META_STORE], 'readwrite', (tx) => {
      tx.objectStore(CHUNK_STORE).clear();
      tx.objectStore(META_STORE).clear();
    });
  } finally {
    db.close();
  }
}

// ── Hervatbare upload (GCS resumable signed URL) ─────────────────────────

// Chunkgrootte moet een veelvoud van 256 KiB zijn (GCS-eis), behalve de
// laatste chunk. Klein gehouden voor mobiel internet (logs 20-10-2026: een
// Android-telefoon op 4G kreeg 10 uploads op rij niet door): de voortgangsbalk
// beweegt zichtbaar, en een chunk past ook op een trage uplink (~45 kbit/s)
// binnen REQUEST_TIMEOUT_MS.
const RESUMABLE_CHUNK_BYTES = 512 * 1024;
// Met de backoff-cap van 16 s houdt een chunk het bij een verbinding die
// direct faalt (offline) ~1,5 min vol: genoeg voor een tunnel of zwak bereik.
// Blijft elk verzoek hangen tot REQUEST_TIMEOUT_MS, dan duurt het tot ~15 min
// voor de foutmelding komt; dat is de bovengrens, niet de norm. De opname
// blijft bij falen op het toestel (crash-buffer).
const MAX_CHUNK_ATTEMPTS = 10;
// Zonder timeout bleef een fetch op een stilgevallen mobiele verbinding
// eeuwig hangen: geen fout, dus ook geen nieuwe poging ("upload loopt vast").
const REQUEST_TIMEOUT_MS = 90_000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

function backoffMs(attempt: number): number {
  return Math.min(1000 * 2 ** (attempt - 1), 16_000);
}

function parseRangeEnd(range: string | null): number | null {
  if (!range) return null;
  const m = /bytes=0-(\d+)/.exec(range);
  return m ? Number(m[1]) : null;
}

/**
 * Start de resumable sessie en geef de sessie-URI terug (Location-header).
 *
 * `uploadHeaders` is de enige bron van de Content-Type: de backend levert daar
 * het basistype in dat óók in de handtekening zit. De client mag er niets bij
 * zetten. WAAROM zo strikt: headerobjecten met verschillend gespelde sleutels
 * ('content-type' naast 'Content-Type') overleven allebei een spread, en de
 * `Headers`-constructor van fetch voegt ze vervolgens samen tot
 * "audio/webm, audio/webm". Dat is niet wat er ondertekend is, dus GCS antwoordt
 * 403 SignatureDoesNotMatch en de upload sterft na zes pogingen.
 */
async function initiateSession(
  uploadUrl: string,
  uploadHeaders: Record<string, string>,
): Promise<string> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt++) {
    try {
      const res = await fetchWithTimeout(uploadUrl, {
        method: 'POST',
        headers: { 'x-goog-resumable': 'start', ...uploadHeaders },
      });
      if (!res.ok) throw new Error(`kon upload-sessie niet starten (HTTP ${res.status})`);
      const location = res.headers.get('location') ?? res.headers.get('Location');
      if (!location) throw new Error('upload-sessie gaf geen sessie-URI terug');
      return location;
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_CHUNK_ATTEMPTS) await delay(backoffMs(attempt));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('kon upload-sessie niet starten');
}

/** Vraag GCS hoeveel bytes al ontvangen zijn (na een netwerkfout). */
async function queryOffset(sessionUri: string, total: number): Promise<number> {
  const res = await fetchWithTimeout(sessionUri, {
    method: 'PUT',
    headers: { 'content-range': `bytes */${total}` },
  });
  if (res.status === 200 || res.status === 201) return total;
  if (res.status === 308) {
    const end = parseRangeEnd(res.headers.get('range'));
    return end === null ? 0 : end + 1;
  }
  throw new Error(`kon upload-status niet opvragen (HTTP ${res.status})`);
}

/** PUT één chunk; geef de nieuwe (server-bevestigde) offset terug. */
async function putChunk(
  sessionUri: string,
  slice: Blob,
  start: number,
  end: number,
  total: number,
  isLast: boolean,
): Promise<number> {
  const res = await fetchWithTimeout(sessionUri, {
    method: 'PUT',
    headers: { 'content-range': `bytes ${start}-${end - 1}/${total}` },
    body: slice,
  });
  if (isLast) {
    if (res.status === 200 || res.status === 201) return total;
    throw new Error(`upload voltooien mislukt (HTTP ${res.status})`);
  }
  if (res.status === 308) {
    const confirmed = parseRangeEnd(res.headers.get('range'));
    return confirmed === null ? end : confirmed + 1;
  }
  throw new Error(`upload-chunk mislukt (HTTP ${res.status})`);
}

/**
 * Upload `blob` hervatbaar naar de resumable signed URL. Bij een netwerk- of
 * serverfout: backoff, vraag de server-offset op en hervat vanaf daar.
 * `onProgress(loaded, total)` rapporteert per chunk voor de voortgangsbalk.
 *
 * `uploadHeaders` komt ongewijzigd uit het antwoord van de upload-URL-route en
 * gaat ongewijzigd naar GCS; zie `initiateSession`. Bewust zonder standaardwaarde,
 * want een lege set headers is geen bruikbare upload maar een 403.
 */
export async function resumableUpload(
  uploadUrl: string,
  blob: Blob,
  onProgress: (loaded: number, total: number) => void,
  uploadHeaders: Record<string, string>,
): Promise<void> {
  const total = blob.size;
  const sessionUri = await initiateSession(uploadUrl, uploadHeaders);
  let offset = 0;
  let attempt = 0;
  onProgress(0, total);
  while (offset < total) {
    const end = Math.min(offset + RESUMABLE_CHUNK_BYTES, total);
    const isLast = end === total;
    try {
      offset = await putChunk(sessionUri, blob.slice(offset, end), offset, end, total, isLast);
      attempt = 0;
      onProgress(offset, total);
    } catch (err) {
      attempt += 1;
      if (attempt >= MAX_CHUNK_ATTEMPTS) throw err;
      await delay(backoffMs(attempt));
      // Hersynchroniseer met de server; hervat vanaf wat daar al binnen is.
      try {
        offset = await queryOffset(sessionUri, total);
        onProgress(offset, total);
      } catch {
        /* server niet bereikbaar; retry-lus probeert dezelfde chunk opnieuw */
      }
    }
  }
}
